import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { ProductType } from '@tink/core/src/types';
import { ReactNode, useEffect, useState } from 'react';
import { MarketCode } from '@tink/config/src/markets';
import { useLegal } from '../../../components/ApplicationProviders/LegalProvider/LegalProvider';
import { useAnalytics } from '../../../core/Analytics/AnalyticsProvider';
import { useConfiguration } from '../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { useUrlQuery } from '../../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { LoadingOverlay } from '../../../core/screens/LoadingOverlay/LoadingOverlay';
import { ProductDimensionPay } from '../../../models/product';
import { useAppSelector } from '../../../stores/redux-store';
import {
  hasHiddenTermsAndScopeMessages,
  isConsentFlow,
  isMerchantNetflixConsentFlow,
} from '../../../models/configuration';
import { PageView, UserInteractionEvent } from '../../../models/analytics';
import { useProduct } from '../../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { useGradualRollout } from '../../../utilities/useGradualRollout';
import { GradualReleaseLabels } from '../../../utilities/useGradualRolloutTypes';
import {
  NetflixConsentScreen as NetflixConsentScreenImport,
  NetflixConsentScreenProps,
} from './NetflixConsentScreen';
import {
  PayMerchantConsentScreen as PayMerchantConsentScreenImport,
  PayMerchantConsentScreenProps,
} from './PayMerchantConsentScreen';
import { ConsentView } from './ConsentView';
import { PaymentConsentScreen } from './PaymentConsentScreen';
import { bankingConsentProductTypes } from './consent-utils';

export const ERR_CONSENT_SCREEN_NOT_SUPPORTED = {
  errorReason: ErrorReason.CONSENT_SCREEN_NOT_SUPPORTED,
  errorStatus: ErrorStatus.INTERNAL_ERROR,
};

type ConsentScreenDirectorProps = {
  children?: ReactNode;
  PayMerchantConsentScreen?: React.ComponentType<PayMerchantConsentScreenProps>;
  NetflixConsentScreen?: React.ComponentType<NetflixConsentScreenProps>;
};

export const ConsentDirector: React.FC<ConsentScreenDirectorProps> = ({
  children,
  NetflixConsentScreen = NetflixConsentScreenImport,
  PayMerchantConsentScreen = PayMerchantConsentScreenImport,
}) => {
  const { tinklinkAnalytics } = useAnalytics();
  const { setLegalDisclaimerPageView, hasUserReadLegalDisclaimer } = useLegal();
  const [isMerchantConsentFlow, setIsMerchantConsentFlow] = useState<boolean | undefined>(
    undefined
  );
  const { data: config } = useConfiguration();
  const { productType, productDimension } = useProduct();
  const isConsentFlowFlag = isConsentFlow(config);
  const isMerchantNetflixConsentFlowFlag = isMerchantNetflixConsentFlow(config);
  const notUsingTinkLicenseFlag = hasHiddenTermsAndScopeMessages(config);
  const { market, sessionId, clientId, providerName } = useUrlQuery();
  const { data: sessionData, loading: sessionLoading } = useAppSelector((state) => state.session);
  const clientAppVerified = useAppSelector((state) => state.oauth.description?.verified);
  const clientName = useAppSelector((state) => state.oauth.description?.clientName);
  const scopesDescriptionsList = useAppSelector(
    (state) => state.oauth.description?.scopesDescriptionsList
  );
  const userId = useAppSelector((state) => state.user.user?.id);

  const canUseMerchantConsentFlow = Boolean(
    sessionId &&
      productType === ProductType.PAY &&
      productDimension === ProductDimensionPay.DIRECT &&
      isConsentFlowFlag
  );

  const shouldUseNetflixMerchantFlow = Boolean(
    market === MarketCode.DE &&
      sessionId &&
      productType === ProductType.PAY &&
      productDimension === ProductDimensionPay.DIRECT &&
      isMerchantNetflixConsentFlowFlag &&
      notUsingTinkLicenseFlag
  );

  // only bulk payments use PCS by default for now
  const shouldUseBulkPaymentConsentFlow = Boolean(
    productType === ProductType.PAY && productDimension === ProductDimensionPay.BULK
  );

  /*
   * We enabled the new banking consent screen
   * if it's either enabled via feature flag or if the market
   * is US. Only for certain products.
   */
  const shouldUseBankingConsentFlow = Boolean(
    bankingConsentProductTypes.includes(productType) &&
      (isConsentFlowFlag || market === MarketCode.US)
  );

  const { isInTestGroup: isInPaymentConsentScreenTestGroup } = useGradualRollout(
    PageView.NO_SCREEN,
    GradualReleaseLabels.PAYMENT_CONSENT_SCREEN_DE_50
  );

  const shouldShowPaymentConsentScreenFlag = Boolean(
    productType === ProductType.PAY &&
      productDimension === ProductDimensionPay.DIRECT &&
      isConsentFlowFlag
  );

  const { isInTestGroup: isInPaymentConsentScreenProvidersGroup } = useGradualRollout(
    PageView.NO_SCREEN,
    GradualReleaseLabels.PAYMENT_CONSENT_SCREEN_100
  );

  const shouldUsePaymentConsentScreenPerClientId = isInPaymentConsentScreenTestGroup({
    userId: userId || '',
    market,
    productType,
    productDimension,
    clientId,
  });

  const shouldUsePaymentConsentScreenPerProviderName =
    providerName &&
    isInPaymentConsentScreenProvidersGroup({
      userId: userId || '',
      productType,
      productDimension,
      providerName,
    });

  const shouldUseConsentFlow =
    canUseMerchantConsentFlow ||
    shouldUseBulkPaymentConsentFlow ||
    shouldUsePaymentConsentScreenPerClientId ||
    shouldUsePaymentConsentScreenPerProviderName ||
    shouldShowPaymentConsentScreenFlag ||
    shouldUseBankingConsentFlow;

  useEffect(() => {
    if (sessionData && !sessionLoading) {
      if (sessionData?.merchantId) {
        setIsMerchantConsentFlow(true);
      } else {
        setIsMerchantConsentFlow(false);
      }
    }
  }, [sessionData, sessionLoading]);
  // we have to check for NO_SCREEN because this will ensure rendering children after clicking continue button
  const hasConsentBeenGiven = hasUserReadLegalDisclaimer(PageView.NO_SCREEN);
  const isAbleToShowMerchantConsent =
    canUseMerchantConsentFlow && isMerchantConsentFlow && !hasConsentBeenGiven;

  const handleConsentGiven = () => {
    tinklinkAnalytics.sendInteractionEvent({
      action: `${PageView.CONSENT_SCREEN}/${UserInteractionEvent.SUBMIT_CONSENT}`,
      view: PageView.CONSENT_SCREEN,
    });
    setLegalDisclaimerPageView(PageView.CONSENT_SCREEN);
  };

  if (sessionId && !sessionData) {
    return <LoadingOverlay />;
  }

  if (isAbleToShowMerchantConsent) {
    if (shouldUseNetflixMerchantFlow) {
      return (
        <NetflixConsentScreen
          onConsent={handleConsentGiven}
          isClientVerified={Boolean(clientAppVerified)}
        />
      );
    }

    return (
      <PayMerchantConsentScreen
        clientName={clientName || ''}
        onConsent={handleConsentGiven}
        isClientVerified={Boolean(clientAppVerified)}
      />
    );
  }

  if (
    (shouldUsePaymentConsentScreenPerClientId ||
      shouldUsePaymentConsentScreenPerProviderName ||
      shouldShowPaymentConsentScreenFlag ||
      shouldUseBulkPaymentConsentFlow) &&
    clientName &&
    !hasConsentBeenGiven
  ) {
    return (
      <PaymentConsentScreen
        clientName={clientName}
        isClientVerified={Boolean(clientAppVerified)}
        onConsent={handleConsentGiven}
      />
    );
  }

  if (shouldUseBankingConsentFlow && clientName && !hasConsentBeenGiven && scopesDescriptionsList) {
    return (
      <ConsentView
        onConfirm={handleConsentGiven}
        clientName={clientName}
        scopesDescriptionsList={scopesDescriptionsList}
        productType={productType}
      />
    );
  }

  // Opt-out to default handling / Not using consent flow
  if (
    (hasConsentBeenGiven && shouldUseConsentFlow) ||
    !shouldUseConsentFlow ||
    (canUseMerchantConsentFlow && isMerchantConsentFlow === false)
  ) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return <LoadingOverlay />;
};
